import { logger } from "@hopper-b2b/api";
import { SliceStopCountFilter, TripCategory } from "@hopper-b2b/types";

/**
 * Resolve the `tripCategory` query parameter to write into the URL based on
 * the current state and the parsed query string.
 *
 * Behaviour:
 * - When the URL explicitly sets `tripCategory`, prefer that value.
 * - Otherwise, fall back to the state's `tripCategory`.
 * - When state says `ROUND_TRIP` but no return date exists in either state
 *   or URL, the URL is semantically a one-way query (e.g. FlightWatch deep
 *   links): infer `ONE_WAY` rather than producing a round-trip with a
 *   missing return date.
 *
 * Note: when the URL explicitly says `ROUND_TRIP` but provides no return
 * date, we preserve the explicit intent rather than silently flipping to
 * one-way, and emit a warning so the inconsistency is observable in
 * Datadog.
 *
 * Return-date inputs are typed as `Date | null | undefined` to match what
 * `parseQueryString` and the search reducer actually produce; this avoids
 * the empty-string ambiguity that a wider `string` type would introduce.
 */
export const inferTripCategoryForQuery = ({
  stateTripCategory,
  stateReturnDate,
  queryTripCategory,
  queryReturnDate,
}: {
  stateTripCategory: TripCategory | undefined;
  stateReturnDate: Date | null | undefined;
  queryTripCategory: TripCategory | undefined;
  queryReturnDate: Date | null | undefined;
}): TripCategory | undefined => {
  const hasReturnDate = stateReturnDate != null || queryReturnDate != null;

  if (stateTripCategory === TripCategory.ROUND_TRIP && !hasReturnDate) {
    if (queryTripCategory === TripCategory.ROUND_TRIP) {
      logger()?.warn(
        "[populateFlightShopQueryParams] queryString.tripCategory is round_trip but no returnDate is present"
      );
      return queryTripCategory;
    }
    return queryTripCategory ?? TripCategory.ONE_WAY;
  }

  return stateTripCategory ?? queryTripCategory;
};

/**
 * Decide whether the current shop session is a round trip.
 *
 * `isRoundTrip` gates the `ChooseReturn` step. Reading it from the search
 * store alone is racy: the store is hydrated from the URL asynchronously, so a
 * user who taps an outbound flight before hydration finishes would see
 * `tripCategory === undefined`, skip `ChooseReturn`, and get an unselected
 * return leg auto-bundled. The URL query is authoritative from the first
 * render, so we treat a round-trip signal from EITHER the store or the URL
 * (trip category or the presence of a return date) as a round trip.
 *
 * A genuine one-way (one_way in the URL, no return date) still resolves to
 * false, so one-way deep links / FlightWatch entries are unaffected.
 */
export const isRoundTripSelection = ({
  stateTripCategory,
  stateReturnDate,
  queryTripCategory,
  queryReturnDate,
}: {
  stateTripCategory: TripCategory | undefined;
  stateReturnDate: Date | null | undefined;
  queryTripCategory: TripCategory | undefined;
  queryReturnDate: Date | null | undefined;
}): boolean =>
  stateTripCategory === TripCategory.ROUND_TRIP ||
  queryTripCategory === TripCategory.ROUND_TRIP ||
  stateReturnDate != null ||
  queryReturnDate != null;

/**
 * Coerce a free-form `stopsOption` value (typically read from a URL query
 * param) into a valid `SliceStopCountFilter` enum value. Falls back to
 * `ANY_NUMBER` when the input is missing or not a recognised enum value,
 * so a malformed deep link cannot smuggle an invalid value into the
 * application state via a bare type cast.
 */
export const coerceStopsOption = (
  value: string | undefined | null
): SliceStopCountFilter => {
  const validValues = Object.values(SliceStopCountFilter) as string[];
  if (value && validValues.includes(value)) {
    return value as SliceStopCountFilter;
  }
  return SliceStopCountFilter.ANY_NUMBER;
};
